import { AUTHOR, DATE_READ, PUBLISHED, RATING, SHELF_DATE, TITLE, authorSurname, shelfDate } from '../../../bookSort.js';

// What a bookcase is labelled with when a shelf takes more than one of them:
// the span of what is on it, in whatever order the shelf is sorted.
//
// The abbreviations are the ones a library uses on the end of a stack, and
// they are worked out the same way: long enough to tell this bookcase from the
// one beside it, and no longer. Ghandi and Guthrie need two letters to
// separate, so the pair reads A-Gh then Gu-Z. If the next bookcase started at
// Gharial they would need three.
//
// It is a span and not a count on purpose. A count is already on the status
// bar, and what somebody walking past the third of five bookcases wants to
// know is whether the book they are looking for is in this one.

// Four each side is exactly what a bookcase holds: "Abcd-Efgh" is nine
// characters, which at eight pixels a character is 78 against a bookcase 77
// wide. It is a width, not a taste.
const MAX_LETTERS = 4;

// A leading article is not part of a title for filing, which is the same rule
// the shelf sorts by - see titleSortKey. Kept in its original case here,
// because this is printed rather than compared.
const LEADING_ARTICLE = /^(?:a|an|the)\s+/i;

// What the book files under, as text, for the orders that file alphabetically.
function filedAs(book, order) {
  if (order === AUTHOR) return authorSurname(book.author);
  return String(book.title || '').trim().replace(LEADING_ARTICLE, '').trim();
}

// The first letters that can be printed. Punctuation goes, so "O'Brien"
// abbreviates to Ob rather than to O' - the mark is not what tells one name
// from another.
//
// Spaces stay, and that is not a detail. The shelf sorts by the whole filing
// string and localeCompare puts a space before any letter, so Le Guin files
// ahead of Leckie. Dropping the space here abbreviated the pair to Leg and Lec
// and the badge read "Leg-Lec" - backwards, on a bookcase that was in the
// right order. A label that contradicts the shelf it is nailed to is worse
// than no label, so this abbreviates the same string the sort compares.
const abbreviate = (text, letters) => {
  const kept = String(text || '').replace(/[^\p{L}\p{N} ]/gu, '').replace(/\s+/g, ' ').trim();
  if (!kept) return '?';
  // Trimmed again after slicing, so a cut that lands on the space does not
  // print one.
  const cut = kept.slice(0, letters).trim();
  return cut.slice(0, 1).toUpperCase() + cut.slice(1).toLowerCase();
};

// How many letters it takes to tell two filings apart. Identical ones cannot
// be separated at any length - a boundary can fall inside one author - so they
// get the longest abbreviation and read as the same, which is true.
function lettersToSeparate(left, right) {
  for (let n = 1; n <= MAX_LETTERS; n++) {
    if (abbreviate(left, n) !== abbreviate(right, n)) return n;
  }
  return MAX_LETTERS;
}

// A question mark rather than an empty badge: a bookcase of books with no date
// in the field being sorted by is a real case - a To Read shelf under a date
// read order is all of them - and a blank bronze plate reads as a fault where
// "?" reads as "nothing here says".
const yearOf = (value) => String(value || '').slice(0, 4) || '?';
const ratingOf = (book) => book.userRating || book.rating || 0;

// What the date orders read off a book. Shelf date asks the book which of the
// two it files under, so a badge on a To Read bookcase prints the year it was
// added and one on Read prints the year it was finished - the same rule the
// shelf beside it sorted by.
function dateOf(book, order) {
  if (order === SHELF_DATE) return shelfDate(book);
  return order === DATE_READ ? book.dateRead : book.dateAdded;
}

// The orders that do not file alphabetically get the values themselves, since
// a prefix of "2026-04-25T23:21:51Z" is not a thing anybody reads.
function spanOf(first, last, order) {
  if (order === RATING) {
    const show = (book) => (ratingOf(book) ? ratingOf(book).toFixed(1) : 'NR');
    return [show(first), show(last)];
  }
  if (order === PUBLISHED) {
    const show = (book) => (book.publishedYear ? String(book.publishedYear) : '?');
    return [show(first), show(last)];
  }
  // A date, which is the default: shelf date, date read or date added.
  return [yearOf(dateOf(first, order)), yearOf(dateOf(last, order))];
}

// caseRanges takes the books of each bookcase, in shelf order, and gives back
// a label for each - or nulls, when there is only one bookcase and nothing to
// tell apart.
export function caseRanges(cases, order) {
  if (cases.length < 2) return cases.map(() => null);

  const ends = cases.map((books) => ({ first: books[0], last: books[books.length - 1] }));

  if (order === AUTHOR || order === TITLE) {
    const filings = ends.map(({ first, last }) => ({
      start: filedAs(first, order),
      end: filedAs(last, order),
    }));

    // One letter at each end of the shelf, where there is no neighbour to be
    // confused with, and however many the boundary needs everywhere else.
    const letters = filings.map(() => ({ start: 1, end: 1 }));
    for (let i = 0; i < filings.length - 1; i++) {
      const n = lettersToSeparate(filings[i].end, filings[i + 1].start);
      letters[i].end = n;
      letters[i + 1].start = n;
    }

    return filings.map((filing, i) => {
      let { start, end } = letters[i];
      // Each end of a badge is abbreviated to what its own boundary needed, and
      // the two boundaries rarely need the same depth. Across a letter that is
      // fine and is what a stack sign looks like - A-Gh reads correctly. Within
      // one letter it is not: a bookcase running Dinnegan to Duncan came out
      // "Dinn-D", which reads as ending before it starts. So a badge that stays
      // inside one letter is abbreviated to one depth, the deeper of the two.
      if (abbreviate(filing.start, 1) === abbreviate(filing.end, 1)) {
        start = Math.max(start, end);
        end = start;
      }

      const from = abbreviate(filing.start, start);
      const to = abbreviate(filing.end, end);
      // And a bookcase that is all one filing says it once. "W-W" is not a
      // range, it is a bookcase of Ws.
      return from === to ? from : `${from}-${to}`;
    });
  }

  return ends.map(({ first, last }) => {
    const [start, end] = spanOf(first, last, order);
    // A bookcase that is all one year says the year once rather than twice.
    return start === end ? start : `${start}-${end}`;
  });
}
